import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { metaPageView } from "./lib/metaPixel";
import { Toaster } from "./components/ui/sonner";
import TabLockedToast from "./components/TabLockedToast";
import DownloadOverlay from "./components/DownloadOverlay";
import PreviewSandboxBanner from "./components/PreviewSandboxBanner";
import { ErrorBoundary } from "./components/ErrorBoundary";
import FloatingCFOBuck from "./components/FloatingCFOBuck";
import OnboardingTour from "./components/OnboardingTour";
import TrialGateBanner from "./components/TrialGateBanner";
import TrafficTracker from "./components/TrafficTracker";
import ClickTracker from "./components/ClickTracker";
import ConsentBanner from "./components/ConsentBanner";
import ChatWidgetController from "./components/ChatWidgetController";
import MigrationProgressBeacon from "./components/MigrationProgressBeacon";

// Pages
import LandingPage from "./pages/LandingPage";
import LandingPageElite from "./pages/LandingPageElite";
import LandingPageDark from "./pages/LandingPageDark";
import BookAppointment from "./pages/BookAppointment";
import ManageAppointment from "./pages/ManageAppointment";
import LandingPage2026 from "./pages/LandingPage2026";
import LandingPageHub from "./pages/LandingPageHub";
import FeaturesHub from "./pages/FeaturesHub";
import ResourcesHub from "./pages/ResourcesHub";
import ContactHub from "./pages/ContactHub";
import SecurityHub from "./pages/SecurityHub";
import DemoPage from "./pages/DemoPage";
import LoginDark from "./pages/LoginDark";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import FeaturesPage from "./pages/FeaturesPage";
import WelcomeUpsellPage from "./pages/WelcomeUpsellPage";
import TermsOfService from "./pages/TermsOfService";
import AuthCallback from "./pages/AuthCallback";
import ImpersonateStart from "./pages/ImpersonateStart";
import AdminViewStart from "./pages/AdminViewStart";
import MagicLoginPage from "./pages/MagicLoginPage";
import AcceptInvite from "./pages/AcceptInvite";
import CommandCenter from "./pages/CommandCenter";
import DashboardHub from "./pages/DashboardHub";
import ActivityLog from "./pages/ActivityLog";
import ChartOfAccounts from "./pages/ChartOfAccounts";
import JournalEntries from "./pages/JournalEntries";
import JournalEntryDetail from "./pages/JournalEntryDetail";
import CreateJournalEntry from "./pages/CreateJournalEntry";
import GeneralLedger from "./pages/GeneralLedger";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import AuditLog from "./pages/AuditLog";
import Banking from "./pages/Banking";
import BankingMatch from "./pages/BankingMatch";
import AIBuck from "./pages/AIBuck";
import Contacts from "./pages/Contacts";
import ContactDetail from "./pages/ContactDetail";
import ContactForm from "./pages/ContactForm";
import NewTask from "./pages/NewTask";
import InvoiceForm from "./pages/InvoiceForm";
import BillForm from "./pages/BillForm";
import AccountForm from "./pages/AccountForm";
import RecurringEntryForm from "./pages/RecurringEntryForm";
import FolderTemplates from "./pages/FolderTemplates";
import FolderTemplateForm from "./pages/FolderTemplateForm";
import FolderAutomations from "./pages/FolderAutomations";
import WorkspaceSetupWizard from "./pages/WorkspaceSetupWizard";
import PublicFolderView from "./pages/PublicFolderView";
import DocumentsHub from "./pages/DocumentsHub";
import SignaturesHub from "./pages/SignaturesHub";
import PublicSignPage from "./pages/PublicSignPage";
import ClientReplies from "./pages/ClientReplies";
import AICommandCenter from "./pages/AICommandCenter";
import AIDocuments from "./pages/AIDocuments";
import LegalHub from "./pages/hubs/Legal";
import HRHub from "./pages/hubs/HR";
import MarketingHub from "./pages/hubs/Marketing";
import KnowledgeHub from "./pages/hubs/Knowledge";
import CRMHub from "./pages/hubs/CRM";
import FinanceHub from "./pages/hubs/Finance";
import OperationsHub from "./pages/hubs/Operations";
import ProductivityHub from "./pages/hubs/Productivity";
import Invoices from "./pages/Invoices";
import Bills from "./pages/Bills";
import BatchImport from "./pages/BatchImport";
import ReconciliationList from "./pages/ReconciliationList";
import NewReconciliation from "./pages/NewReconciliation";
import BankReconciliation from "./pages/BankReconciliation";
import StatementUpload from "./pages/StatementUpload";
import ReconcileBuck from "./pages/ReconcileBuck";
import AdminPanel from "./pages/AdminPanel";
import InvoiceOCR from "./pages/InvoiceOCR";
import TaxSavingsReport from "./pages/TaxSavingsReport";
import AllConversations from "./pages/AllConversations";
import MigrationHub from "./pages/MigrationHub";
import DSCRCounseling from "./pages/DSCRCounseling";
import CFOBuck from "./pages/CFOBuck";
import StatementImporter from "./pages/StatementImporter";
import TaxMapper from "./pages/TaxMapper";
import ChatHistory from "./pages/ChatHistory";
import TaskManager from "./pages/TaskManager";
import TimeTracking from "./pages/TimeTracking";
import TimeEntryForm from "./pages/TimeEntryForm";
import University from "./pages/University";
import GuidedTours from "./pages/GuidedTours";
import Achievements from "./pages/Achievements";
import Leaderboard from "./pages/Leaderboard";
import RecurringEntries from "./pages/RecurringEntries";
import DocumentInbox from "./pages/DocumentInbox";
import AIHub from "./pages/AIHub";
import MemoryHub from "./pages/MemoryHub";
import AIWorkspace from "./pages/AIWorkspace";

// Client Portal
import ClientPortalLayout from "./pages/portal/ClientPortalLayout";
import PublicPayPage, { PublicPaySuccessPage } from "./pages/PublicPayPage";
import PublicPortalLandingPage from "./pages/PublicPortalLandingPage";
import PublicPortalSignupPage from "./pages/PublicPortalSignupPage";
import PublicPortalLoginPage from "./pages/PublicPortalLoginPage";
import PublicPortalForgotPasswordPage from "./pages/PublicPortalForgotPasswordPage";
import PublicTaxSavingsPage from "./pages/PublicTaxSavingsPage";
import PortalLookupPage from "./pages/PortalLookupPage";
import TeamSettings from "./pages/TeamSettings";
import SupportPage from "./pages/SupportPage";
import AcceptOwnershipPage from "./pages/AcceptOwnershipPage";
import PublicSitePage from "./pages/PublicSitePage";
import MessagesPage from "./pages/MessagesPage";
import InboxPage from "./pages/InboxPage";
import ClientDashboard from "./pages/portal/ClientDashboard";
import ClientInvoices from "./pages/portal/ClientInvoices";
import ClientStatement from "./pages/portal/ClientStatement";
import ClientDocuments from "./pages/portal/ClientDocuments";
import ClientMessages from "./pages/portal/ClientMessages";
import ClientChecklists from "./pages/portal/ClientChecklists";
import Checklists from "./pages/Checklists";

// Owner Admin Components
import { OwnerAdminProvider } from "./context/OwnerAdminContext";
import OwnerAdminLayout from "./components/owner-admin/OwnerAdminLayout";
import OwnerAdminLogin from "./components/owner-admin/OwnerAdminLogin";
import OwnerAdminDashboard from "./components/owner-admin/OwnerAdminDashboard";
import OwnerAdminCustomers from "./components/owner-admin/OwnerAdminCustomers";
import OwnerAdminCustomerDetail from "./components/owner-admin/OwnerAdminCustomerDetail";
import OwnerAdminSeats from "./components/owner-admin/OwnerAdminSeats";
import OwnerAdminUnpaidSweep from "./components/owner-admin/OwnerAdminUnpaidSweep";
import OwnerAdminUsers from "./components/owner-admin/OwnerAdminUsers";
import OwnerAdminAuditLog from "./components/owner-admin/OwnerAdminAuditLog";
import OwnerAdminClientErrors from "./components/owner-admin/OwnerAdminClientErrors";
import OwnerAdminSystemHealth from "./components/owner-admin/OwnerAdminSystemHealth";
import OwnerAdminOperationsHealth from "./components/owner-admin/OwnerAdminOperationsHealth";
import OwnerAdminBilling from "./components/owner-admin/OwnerAdminBilling";
import OwnerAdminStaff from "./components/owner-admin/OwnerAdminStaff";
import OwnerAdminFeatureFlags from "./components/owner-admin/OwnerAdminFeatureFlags";
import OwnerAdminSupport from "./components/owner-admin/OwnerAdminSupport";
import OwnerAdminSettings from "./components/owner-admin/OwnerAdminSettings";
import OwnerAdminGhl from "./components/owner-admin/OwnerAdminGhl";
import OwnerAdminAIUsage from "./components/owner-admin/OwnerAdminAIUsage";
import OwnerAdminFunnelAnalytics from "./components/owner-admin/OwnerAdminFunnelAnalytics";
import OwnerAdminCostDashboard from "./components/owner-admin/OwnerAdminCostDashboard";
import BusinessDashboard from "./pages/BusinessDashboard";
import OwnerAdminTelemetry from "./components/owner-admin/OwnerAdminTelemetry";
import OwnerAdminTraffic from "./components/owner-admin/OwnerAdminTraffic";
import OwnerAdminEmailRules from "./components/owner-admin/OwnerAdminEmailRules";
import OwnerAdminHomepage from "./components/owner-admin/OwnerAdminHomepage";
import OwnerAdminPayments from "./components/owner-admin/OwnerAdminPayments";
import OwnerAdminAppointments from "./components/owner-admin/OwnerAdminAppointments";

// Context
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { WorkspaceProvider } from "./context/WorkspaceContext";

// Layout
import AppLayout from "./components/layout/AppLayout";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, checkAuth, setUser } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(!user); // Only check if no user already

  useEffect(() => {
    // If user data passed from AuthCallback, set user and skip auth check
    if (location.state?.user && !user) {
      setUser(location.state.user);
      setIsChecking(false);
      return;
    }

    // If user already exists in context, skip auth check
    if (user) {
      setIsChecking(false);
      return;
    }

    const verify = async () => {
      await checkAuth();
      setIsChecking(false);
    };
    verify();
  }, [location.state, user, checkAuth, setUser]);

  if (loading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Redirect from /invoices/<id> → /invoices?preview=<id> so old links work.
const InvoicePreviewRedirect = () => {
  const { invoiceId } = useParams();
  return <Navigate to={`/invoices?preview=${invoiceId}`} replace />;
};

// /register → /login?mode=register, preserving UTM / ad-attribution query
// params (gclid, fbclid, utm_*) so paid-traffic tracking survives the hop.
const RegisterRedirect = () => {
  const location = useLocation();
  const search = location.search || '';
  const extra = search.startsWith('?') ? `&${search.slice(1)}` : (search ? `&${search}` : '');
  return <Navigate to={`/login?mode=register${extra}`} replace />;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();

  // Fire a Meta Pixel PageView on every SPA route change. The base code in
  // index.html already fired the initial PageView, so skip the first render.
  const metaFirstLoad = useRef(true);
  useEffect(() => {
    if (metaFirstLoad.current) { metaFirstLoad.current = false; return; }
    metaPageView();
  }, [location.pathname, location.search]);

  // Check for either Emergent's hash-style session_id callback OR the
  // branded Google OAuth `?code=...&state=...` query-style callback
  // (Jun 16, 2026). Both route through <AuthCallback> which dispatches
  // to the correct backend handler.
  if (
    location.hash?.includes('session_id=') ||
    (location.pathname === '/auth/callback' && location.search?.includes('code='))
  ) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPageHub />} />
      {/* Google Ads-friendly subpage alias — some ad campaigns require the
          final URL to be a specific subpath (not the site root). This route
          renders the same landing experience at /home so ads can point at
          www.countbucks.com/home. Root `/` continues to work unchanged. */}
      <Route path="/home" element={<LandingPageHub />} />
      {/* Ad-group message-match landing variants — same layout, hero copy
          tuned to the ad keyword intent (see HERO_VARIANTS in
          LandingPageHub.jsx). Each variant is tracked separately so ad ROI
          can be measured per creative in owner-admin traffic. */}
      <Route path="/home-cpa" element={<LandingPageHub variant="cpa" />} />
      <Route path="/home-bookkeeping" element={<LandingPageHub variant="bookkeeping" />} />
      <Route path="/home-quickbooks" element={<LandingPageHub variant="quickbooks" />} />
      <Route path="/landing-classic" element={<LandingPageDark />} />
      <Route path="/elite" element={<LandingPageElite />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/features" element={<FeaturesHub />} />
      <Route path="/features-classic" element={<FeaturesPage />} />
      <Route path="/resources" element={<ResourcesHub />} />
      <Route path="/contact" element={<ContactHub />} />
      <Route path="/security" element={<SecurityHub />} />
      <Route path="/welcome" element={<WelcomeUpsellPage />} />
      <Route path="/login" element={<LoginDark />} />
      <Route path="/impersonate" element={<ImpersonateStart />} />
      <Route path="/admin-view" element={<AdminViewStart />} />
      <Route path="/magic-login" element={<MagicLoginPage />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/legacy-landing" element={<LandingPage />} />
      <Route path="/legacy-login" element={<LandingPage />} />
      {/* Legacy dashboard routes — forward to the new Command Center so old
          bookmarks / external links don't 404 after the Feb 2026 cleanup. */}
      <Route path="/money-outlook" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard-legacy" element={<Navigate to="/dashboard" replace />} />
      <Route path="/register" element={<RegisterRedirect />} />
      <Route path="/dashboard/legacy" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/business-dashboard"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <BusinessDashboard />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <DashboardHub />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard-classic"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <CommandCenter />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <ActivityLog />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-hub"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <AIHub />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-workspace"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <AIWorkspace />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/all-conversations"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <AllConversations />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/memory"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <MemoryHub />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/switch-from-quickbooks"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <MigrationHub />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/loan-qualification"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <DSCRCounseling />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounts"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <ChartOfAccounts />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounts/new"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <AccountForm />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounts/:accountId/edit"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <AccountForm />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/journal-entries"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <JournalEntries />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/journal-entries/new"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <CreateJournalEntry />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/journal-entries/:entryId"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <JournalEntryDetail />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ledger"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <GeneralLedger />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <Reports />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <Settings />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <TeamSettings />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team/credits"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <TeamSettings />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <SupportPage />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <AuditLog />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/banking"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <Banking />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/banking/transactions/:transactionId/match"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <BankingMatch />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-buck"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <AIBuck />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      {/* Keep legacy route for backwards compatibility */}
      <Route
        path="/banking/ai-buck"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <AIBuck />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/banking/reconcile"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <ReconciliationList />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/banking/reconcile/:reconciliationId"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <BankReconciliation />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/banking/statement-upload"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <StatementUpload />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reconcile-buck"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <ReconcileBuck />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reconcile-buck/new"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <NewReconciliation />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/banking/reconcile/new"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <NewReconciliation />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <AdminPanel />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <Contacts />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      {/* Firm-side client portal message center — surfaces messages
        * that clients send from /portal so the firm has somewhere to
        * see them inside CountBucks (was previously only accessible
        * via API which is why client messages "disappeared"). */}
      <Route
        path="/inbox"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <InboxPage />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      {/* Legacy paths — keep functional but render unified inbox with the
          correct tab pre-selected. */}
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <InboxPage />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts/new"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <ContactForm />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts/:contactId/edit"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <ContactForm />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts/:contactId"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <ContactDetail />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks/new"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <NewTask />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-command"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <AICommandCenter />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/command-center"
        element={<Navigate to="/dashboard" replace />}
      />
      {/* Legacy Command Center route — historical bookmarks redirect to the
        * unified dashboard now. AI Studio lives at /ai-hub. */}
      <Route
        path="/command-center/legacy"
        element={<Navigate to="/dashboard" replace />}
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <DocumentsHub />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/signatures"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <SignaturesHub />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      {/* Public sign page — token IS the auth, no login required. Lives outside
        * the ProtectedRoute / AppLayout shell so unauthenticated signers can
        * complete the flow from their email link. */}
      <Route path="/sign/:token" element={<PublicSignPage />} />
      <Route
        path="/documents/legacy"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <AIDocuments />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-replies"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <InboxPage />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hubs/legal"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <LegalHub />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hubs/hr"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <HRHub />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hubs/marketing"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <MarketingHub />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hubs/knowledge"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <KnowledgeHub />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hubs/crm"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <CRMHub />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hubs/finance"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <FinanceHub />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hubs/operations"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <OperationsHub />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hubs/productivity"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <ProductivityHub />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <Invoices />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/checklists"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <Checklists />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/new"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <InvoiceForm />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:invoiceId/edit"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <InvoiceForm />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      {/* Safety net: any deep-link to /invoices/<id> (without /edit) — e.g.
          old emails, customer bookmarks, third-party automations — now
          redirects to the list with the preview modal auto-opened. */}
      <Route
        path="/invoices/:invoiceId"
        element={<InvoicePreviewRedirect />}
      />
      <Route
        path="/bills"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <Bills />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bills/new"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <BillForm />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bills/:billId/edit"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <BillForm />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoice-ocr"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <InvoiceOCR />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ocr/:jobId/tax-report"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <TaxSavingsReport />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cfo-buck"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <CFOBuck />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/statement-importer"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <StatementImporter />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tax-mapper"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <TaxMapper />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat-history"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <ChatHistory />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/batch-import"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <BatchImport />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <TaskManager />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-tracking"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <TimeTracking />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-tracking/new"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <TimeEntryForm />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-tracking/:entryId/edit"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <TimeEntryForm />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recurring-entries"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <RecurringEntries />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recurring-entries/new"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <RecurringEntryForm />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/folder-templates"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <FolderTemplates />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/folder-templates/new"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <FolderTemplateForm />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/folder-templates/:templateId"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <FolderTemplateForm />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/folder-templates/:templateId/edit"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <FolderTemplateForm />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/folder-automations"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <FolderAutomations />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup-wizard/:workspaceId"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <WorkspaceSetupWizard />
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route path="/p/folder/:token" element={<PublicFolderView />} />
      {/* Public invoice pay pages — co-branded, no auth (token-secured). */}
      <Route path="/pay/:token" element={<PublicPayPage />} />
      <Route path="/pay/:token/success" element={<PublicPaySuccessPage />} />
      {/* Public pre-paid advisor call booking — open to everyone. */}
      <Route path="/book" element={<BookAppointment />} />
      <Route path="/book/success" element={<BookAppointment />} />
      <Route path="/book/manage/:token" element={<ManageAppointment />} />
      {/* Public branded portal landing (slug-based, no auth) — Phase 1 of
        * Full-Scale Client Portal. Once Cloudflare for SaaS is configured,
        * `clients.firmname.com` will CNAME here too. */}
      <Route path="/c/:slug" element={<PublicPortalLandingPage />} />
      <Route path="/c/:slug/start" element={<PublicPortalSignupPage />} />
      <Route path="/c/:slug/login" element={<PublicPortalLoginPage />} />
      <Route path="/c/:slug/forgot-password" element={<PublicPortalForgotPasswordPage />} />
      {/* Public read-only Tax-Savings viewer (shared by CPA with client). */}
      <Route path="/ts/:token" element={<PublicTaxSavingsPage />} />
      {/* Multi-portal recovery for dual-identity users (no slug). */}
      <Route path="/portals/find" element={<PortalLookupPage />} />
      <Route path="/ownership/accept/:token" element={<AcceptOwnershipPage />} />
      {/* Carrd-style public marketing site for a workspace (no auth). */}
      <Route path="/s/:slug" element={<PublicSitePage />} />
      <Route
        path="/document-inbox"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <DocumentInbox />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/university"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <University />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/guided-tours"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <GuidedTours />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/achievements"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <Achievements />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <AppLayout>
                <Leaderboard />
              </AppLayout>
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      />
      
      {/* Client Portal Routes */}
      <Route path="/portal" element={
        <ProtectedRoute>
          <ClientPortalLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ClientDashboard />} />
        <Route path="invoices" element={<ClientInvoices />} />
        <Route path="statement" element={<ClientStatement />} />
        <Route path="documents" element={<ClientDocuments />} />
        <Route path="checklists" element={<ClientChecklists />} />
        <Route path="messages" element={<ClientMessages />} />
      </Route>

      {/* Owner Admin Routes - Separate from customer app */}
      <Route path="/owner-admin/login" element={
        <OwnerAdminProvider>
          <OwnerAdminLogin />
        </OwnerAdminProvider>
      } />
      <Route path="/owner-admin" element={
        <OwnerAdminProvider>
          <OwnerAdminLayout />
        </OwnerAdminProvider>
      }>
        <Route index element={<OwnerAdminDashboard />} />
        <Route path="operations" element={<OwnerAdminOperationsHealth />} />
        <Route path="customers" element={<OwnerAdminCustomers />} />
        <Route path="customers/:workspaceId" element={<OwnerAdminCustomerDetail />} />
        <Route path="seats" element={<OwnerAdminSeats />} />
        <Route path="unpaid-sweep" element={<OwnerAdminUnpaidSweep />} />
        <Route path="users" element={<OwnerAdminUsers />} />
        <Route path="billing" element={<OwnerAdminBilling />} />
        <Route path="ai-usage" element={<OwnerAdminAIUsage />} />
        <Route path="funnel-analytics" element={<OwnerAdminFunnelAnalytics />} />
        <Route path="cost-dashboard" element={<OwnerAdminCostDashboard />} />
        <Route path="telemetry" element={<OwnerAdminTelemetry />} />
        <Route path="traffic" element={<OwnerAdminTraffic />} />
        <Route path="support" element={<OwnerAdminSupport />} />
        <Route path="system" element={<OwnerAdminSystemHealth />} />
        <Route path="features" element={<OwnerAdminFeatureFlags />} />
        <Route path="audit" element={<OwnerAdminAuditLog />} />
        <Route path="client-errors" element={<OwnerAdminClientErrors />} />
        <Route path="email-rules" element={<OwnerAdminEmailRules />} />
        <Route path="staff" element={<OwnerAdminStaff />} />
        <Route path="settings" element={<OwnerAdminSettings />} />
        <Route path="ghl" element={<OwnerAdminGhl />} />
        <Route path="homepage" element={<OwnerAdminHomepage />} />
        <Route path="payments" element={<OwnerAdminPayments />} />
        <Route path="appointments" element={<OwnerAdminAppointments />} />
      </Route>
    </Routes>
  );
};

// ── Canonical app hosts ────────────────────────────────────────────────────
// Anything NOT in this list (or matching one of these suffixes) is treated
// as a CUSTOM DOMAIN — the entire app becomes a single public-site renderer
// keyed by Host header. This is what makes `yourcpa.com` serve the right
// customer's site.
const APP_HOST_SUFFIXES = [
  'countbucks.com',
  'emergentagent.com',
  'emergent.app',
  'emergent.host',
  'localhost',
  '127.0.0.1',
];

function isCustomDomain() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  if (!host) return false;
  return !APP_HOST_SUFFIXES.some((s) => host === s || host.endsWith(`.${s}`));
}

function App() {
  // If the visitor is on a custom domain, render only the public site.
  if (isCustomDomain()) {
    return (
      <ErrorBoundary>
        <BrowserRouter>
          <PublicSitePage hostMode />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </ErrorBoundary>
    );
  }
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <TrafficTracker />
            <ClickTracker />
            <ConsentBanner />
            <ChatWidgetController />
            <PreviewSandboxBanner />
            <TrialGateBanner />
            <AppRouter />
            <FloatingCFOBuck />
            <OnboardingTour />
            <MigrationProgressBeacon />
            <Toaster position="top-right" richColors offset={{ top: 64 }} mobileOffset={{ top: 64 }} />
            <TabLockedToast />
            <DownloadOverlay />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
